import firebase from '../config';

class ImageUploadService {
  constructor() {
    this.storage = firebase.storage();
    this.storageRef = this.storage.ref();
  }

  /**
   * Upload multiple images to Firebase Storage
   * @param {Array} images - Array of image objects with file and metadata
   * @param {string} examCode - Exam code for organization
   * @param {string} questionId - Question ID
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of uploaded image objects with URLs
   */
  async uploadImages(images, examCode, questionId, userId) {
    try {
      const uploadPromises = images.map(async (image) => {
        if (image.uploaded && image.url) {
          // Image already uploaded, return existing data
          return image;
        }

        const imageId = this.generateImageId();
        const fileExtension = image.file.name.split('.').pop();
        const fileName = `${examCode}/${questionId}/${userId}/${imageId}.${fileExtension}`;
        
        const imageRef = this.storageRef.child(fileName);
        
        // Upload the image
        const snapshot = await imageRef.put(image.file);
        
        // Get the download URL
        const downloadURL = await snapshot.ref.getDownloadURL();
        
        // Return updated image object
        return {
          ...image,
          id: imageId,
          url: downloadURL,
          uploaded: true,
          fileName: fileName,
          uploadTime: new Date().toISOString()
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      return uploadedImages;
    } catch (error) {
      throw new Error('Failed to upload images. Please try again.');
    }
  }

  /**
   * Upload a single image
   * @param {Object} image - Image object with file and metadata
   * @param {string} examCode - Exam code
   * @param {string} questionId - Question ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Uploaded image object with URL
   */
  async uploadSingleImage(image, examCode, questionId, userId) {
    try {
      if (image.uploaded && image.url) {
        return image;
      }

      const imageId = this.generateImageId();
      const fileExtension = image.file.name.split('.').pop();
      const fileName = `${examCode}/${questionId}/${userId}/${imageId}.${fileExtension}`;
      
      const imageRef = this.storageRef.child(fileName);
      
      // Upload the image
      const snapshot = await imageRef.put(image.file);
      
      // Get the download URL
      const downloadURL = await snapshot.ref.getDownloadURL();
      
      return {
        ...image,
        id: imageId,
        url: downloadURL,
        uploaded: true,
        fileName: fileName,
        uploadTime: new Date().toISOString()
      };
    } catch (error) {
      throw new Error('Failed to upload image. Please try again.');
    }
  }

  /**
   * Delete images from Firebase Storage
   * @param {Array} images - Array of image objects with URLs
   * @returns {Promise<void>}
   */
  async deleteImages(images) {
    try {
      const deletePromises = images
        .filter(image => image.url && image.uploaded)
        .map(async (image) => {
          const imageRef = this.storage.refFromURL(image.url);
          await imageRef.delete();
        });

      await Promise.all(deletePromises);
    } catch (error) {
      throw new Error('Failed to delete images.');
    }
  }

  /**
   * Validate image file
   * @param {File} file - File to validate
   * @returns {Object} Validation result with isValid and error message
   */
  validateImage(file) {
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Invalid file type. Please upload JPG, PNG, GIF, or WebP images.'
      };
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File size too large. Please upload images smaller than 5MB.'
      };
    }

    return { isValid: true };
  }

  /**
   * Get image metadata
   * @param {File} file - Image file
   * @returns {Promise<Object>} Image metadata
   */
  async getImageMetadata(file) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          size: file.size,
          type: file.type,
          name: file.name,
          aspectRatio: img.width / img.height
        });
      };
      img.onerror = () => {
        resolve({
          size: file.size,
          type: file.type,
          name: file.name,
          error: 'Could not load image metadata'
        });
      };
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Compress image before upload (optional)
   * @param {File} file - Image file to compress
   * @param {number} quality - Compression quality (0-1)
   * @param {number} maxWidth - Maximum width
   * @param {number} maxHeight - Maximum height
   * @returns {Promise<File>} Compressed image file
   */
  async compressImage(file, quality = 0.8, maxWidth = 1920, maxHeight = 1080) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
          resolve(compressedFile);
        }, file.type, quality);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Generate unique image ID
   * @returns {string} Unique image ID
   */
  generateImageId() {
    return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get storage usage for user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Storage usage information
   */
  async getStorageUsage(userId) {
    try {
      // This is a simplified version - in production you might want to track usage in database
      const userRef = this.storageRef.child(userId);
      const result = await userRef.listAll();
      
      let totalSize = 0;
      const files = [];
      
      for (const item of result.items) {
        const metadata = await item.getMetadata();
        totalSize += metadata.size;
        files.push({
          name: item.name,
          size: metadata.size,
          url: await item.getDownloadURL()
        });
      }
      
      return {
        totalSize,
        fileCount: files.length,
        files
      };
    } catch (error) {
      return { totalSize: 0, fileCount: 0, files: [] };
    }
  }

  /**
   * Clean up unused images
   * @param {string} examCode - Exam code
   * @param {Array} usedImageUrls - Array of image URLs that are still in use
   * @returns {Promise<void>}
   */
  async cleanupUnusedImages(examCode, usedImageUrls) {
    try {
      const examRef = this.storageRef.child(examCode);
      const result = await examRef.listAll();
      
      const deletePromises = result.items
        .filter(async (item) => {
          const url = await item.getDownloadURL();
          return !usedImageUrls.includes(url);
        })
        .map(async (item) => {
          await item.delete();
        });

      await Promise.all(deletePromises);
    } catch (error) {
    }
  }
}

export default new ImageUploadService(); 